import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { notify } from '../../helpers/utils';
import { getUserData } from '../../helpers/user';
import { getAuthHeader } from '../../helpers/auth';
import MarkPaidModal from './MarkPaidModal';
import {
  Phone, Calendar, Search, Filter, X, Eye, Zap,
  CreditCard, CheckCircle, AlertTriangle, Clock,
  Briefcase, Users, ChevronUp, ChevronDown,
  ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight,
  Loader, List
} from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  surf3:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  faint:   'var(--nd-w10)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  green:   '#34D399',
  cyan:    '#22D3EE',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes modalIn { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }

  /* ── inputs ── */
  .sol-input, .sol-date {
    width: 100%; padding: 9px 12px 9px 34px;
    background: var(--nd-w05); border: 1px solid var(--nd-w10);
    border-radius: 9px; color: var(--nd-text);
    font-size: 12px; font-family: 'DM Sans', sans-serif;
    outline: none; transition: border-color 0.15s, background 0.15s;
    color-scheme: dark;
  }
  .sol-date { padding-left: 34px; }
  .sol-input::placeholder { color: var(--nd-tx22); }
  .sol-input:focus, .sol-date:focus {
    border-color: rgba(59,130,246,0.55); background: rgba(59,130,246,0.05);
  }

  /* ── checkbox ── */
  .sol-checkbox-wrap {
    display: flex; align-items: center; gap: 9px; cursor: pointer;
    padding: 9px 13px; border-radius: 9px;
    background: var(--nd-w04); border: 1px solid var(--nd-w08);
    transition: background 0.15s;
  }
  .sol-checkbox-wrap:hover { background: var(--nd-w08); }
  .sol-checkbox { width: 15px; height: 15px; accent-color: #3B82F6; cursor: pointer; }

  /* ── table ── */
  .sol-th {
    padding: 10px 13px; font-size: 9px; font-weight: 700; letter-spacing: 0.12em;
    text-transform: uppercase; color: var(--nd-tx30);
    font-family: 'DM Sans', sans-serif; border-bottom: 1px solid var(--nd-w08);
    white-space: nowrap; background: var(--nd-surface-2);
  }
  .sol-th-sort { cursor: pointer; user-select: none; }
  .sol-th-sort:hover { color: var(--nd-tx55); }
  .sol-tr { border-bottom: 1px solid var(--nd-w05); transition: background 0.12s; }
  .sol-tr:last-child { border-bottom: none; }
  .sol-tr:hover td { background: var(--nd-w03); }
  .sol-td { padding: 11px 13px; vertical-align: middle; }

  /* ── buttons ── */
  .sol-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 12px; border-radius: 7px; font-size: 11px; font-weight: 700;
    font-family: 'DM Sans', sans-serif; cursor: pointer; transition: all 0.15s;
    border: 1px solid; white-space: nowrap;
  }
  .sol-btn-apply  { background: linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong); border-color:transparent; box-shadow:0 4px 12px rgba(59,130,246,0.25); }
  .sol-btn-apply:hover { opacity:.88; }
  .sol-btn-clear  { background: var(--nd-w05); color:var(--nd-tx45); border-color:var(--nd-w10); }
  .sol-btn-clear:hover { background: var(--nd-w08); color:var(--nd-tx70); }
  .sol-btn-view   { background: rgba(59,130,246,0.10); color:#60A5FA; border-color:rgba(59,130,246,0.25); }
  .sol-btn-view:hover { background: rgba(59,130,246,0.18); border-color:rgba(59,130,246,0.45); }
  .sol-btn-gen    { background: rgba(52,211,153,0.10); color:#34D399; border-color:rgba(52,211,153,0.25); }
  .sol-btn-gen:hover { background: rgba(52,211,153,0.18); border-color:rgba(52,211,153,0.45); }
  .sol-btn-pay    { background: rgba(34,211,165,0.10); color:#22D3A5; border-color:rgba(34,211,165,0.25); }
  .sol-btn-pay:hover { background: rgba(34,211,165,0.18); border-color:rgba(34,211,165,0.45); }

  /* ── pagination ── */
  .sol-page-btn {
    width: 30px; height: 30px; border-radius: 7px; border: 1px solid var(--nd-w10);
    background: var(--nd-w04); color: var(--nd-tx45);
    font-size: 11px; font-weight: 700; font-family: 'DM Mono', monospace;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
  }
  .sol-page-btn:hover:not(:disabled) { background: var(--nd-w08); color:var(--nd-text); }
  .sol-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }
  .sol-page-btn.active {
    background: linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong);
    border-color: transparent; box-shadow: 0 3px 10px rgba(59,130,246,0.3);
  }

  /* ── modal overlay ── */
  .sol-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.72);
    backdrop-filter: blur(4px); z-index: 1050;
    display: flex; align-items: center; justify-content: center; padding: 20px;
  }
  .sol-modal {
    background: var(--nd-surface); border: 1px solid var(--nd-w10);
    border-radius: 16px; width: 100%; max-width: 720px; max-height: 85vh;
    display: flex; flex-direction: column;
    box-shadow: 0 24px 60px rgba(0,0,0,0.7);
    animation: modalIn 0.2s ease;
  }
  .sol-modal-body { overflow-y: auto; padding: 20px; flex: 1; }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

// ── Status badge config ────────────────────────────────────────────
const statusCfg = (status) => ({
  Pending:     { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.28)'  },
  Completed:   { color: '#34D399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.28)'  },
  Cancelled:   { color: '#F87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.28)' },
  'In Progress':{ color: '#22D3EE', bg:'rgba(34,211,238,0.12)',  border: 'rgba(34,211,238,0.28)'  },
}[status] || { color:'var(--nd-tx35)', bg:'var(--nd-w05)', border:'var(--nd-w12)' });

const StatusBadge = ({ status }) => {
  const c = statusCfg(status);
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 5,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      fontFamily: T.mono, background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      {status}
    </span>
  );
};

// ── Section head inside modal ──────────────────────────────────────
const ModalSecHead = ({ Icon, color, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
    <div style={{ width: 26, height: 26, borderRadius: 7, background: `${color}15`, border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={13} color={color} />
    </div>
    <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{title}</span>
  </div>
);

// ══════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const SalesOrdersList = () => {
  const [orders, setOrders]           = useState([]);
  const [totalPages, setTotalPages]   = useState(1);
  const [loading, setLoading]         = useState(true);
  const [searchPhone, setSearchPhone] = useState('');
  const [debouncedPhone, setDebouncedPhone] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [noJobOrders, setNoJobOrders] = useState(false);

  const [showJobOrdersModal, setShowJobOrdersModal]   = useState(false);
  const [currentSalesOrderId, setCurrentSalesOrderId] = useState(null);
  const [loadingJobOrders, setLoadingJobOrders]       = useState(false);
  const [jobOrders, setJobOrders]                     = useState([]);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');

  const [editingJobId, setEditingJobId]         = useState(null);
  const [teamInput, setTeamInput]               = useState('');
  const [editingDateJobId, setEditingDateJobId] = useState(null);
  const [editedDate, setEditedDate]             = useState('');

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason]       = useState('');
  const [jobToCancel, setJobToCancel]         = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);

  const authHeader = getAuthHeader();

  // Debounce phone
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedPhone(searchPhone.trim()); setCurrentPage(1); }, 500);
    return () => clearTimeout(t);
  }, [searchPhone]);

  useEffect(() => {
    fetchSalesOrders(currentPage, debouncedPhone, noJobOrders, startDate, endDate);
  }, [currentPage, debouncedPhone, noJobOrders]);

  const fetchSalesOrders = async (pageNumber = 1, phone = '', noJobOrdersFlag = false) => {
    setLoading(true);
    try {
      const params = { page: pageNumber, phone, noJobOrders: noJobOrdersFlag, startDate, endDate };
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/sales/sales-orders`, { params });
      if (res.data && res.data.data) {
        setOrders(res.data.data);
        setTotalPages(res.data.totalPages);
      } else {
        notify('error', 'Invalid API response structure');
      }
    } catch (err) {
      notify('error', `API Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobOrders = async (salesOrderId) => {
    setLoadingJobOrders(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/job-orders/by-sales-order/${salesOrderId}`);
      if (Array.isArray(res.data)) setJobOrders(res.data);
      else if (res.data && Array.isArray(res.data.jobOrders)) setJobOrders(res.data.jobOrders);
      else setJobOrders([]);
    } catch (err) {
      notify('error', `Error fetching job orders: ${err.response?.data?.message || err.message}`);
      setJobOrders([]);
    } finally {
      setLoadingJobOrders(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleViewJobOrderButtonClick = (salesOrderId) => {
    setCurrentSalesOrderId(salesOrderId);
    setShowJobOrdersModal(true);
    fetchJobOrders(salesOrderId);
  };

  const handleGenerateJobOrderButtonClick = async (salesOrderId, initialCleaningDate, orderPackage) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/job-orders/generate`, {
        salesOrderId, cleaningDate: initialCleaningDate, frequency: orderPackage,
      });
      if (response.data.success) {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/notifications/notify-operations`, {
          title: 'New Job Order Created',
          body: `Job order created for Sales Order ID: ${salesOrderId}`,
        });
      }
      setCurrentSalesOrderId(salesOrderId);
      fetchJobOrders(salesOrderId);
      setShowJobOrdersModal(true);
      notify('success', 'Job Order Created');
    } catch (error) {
      notify('error', `Error generating job orders: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setOrders(prev => [...prev].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    }));
  };

  const handleShowMarkPaidModal = (id) => { setCurrentSalesOrderId(id); setShowMarkPaidModal(true); };

  const handleClearFilters = () => {
    setStartDate(''); setEndDate(''); setSearchPhone(''); setNoJobOrders(false); setCurrentPage(1);
    fetchSalesOrders(1, '', false, '', '');
  };

  // ── Pagination page numbers ─────────────────────────────────────
  const maxPagesToShow = 10;
  const startPage = Math.floor((currentPage - 1) / maxPagesToShow) * maxPagesToShow + 1;
  const endPage   = Math.min(startPage + maxPagesToShow - 1, totalPages);

  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px' }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ─────────────────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px' }}>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · SALES</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Sales Orders Management</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Track and manage all sales orders</div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* ── FILTERS CARD ────────────────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, marginBottom: 16, overflow: 'hidden' }}>
        {/* head */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Filter size={13} color={T.blue} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Search &amp; Filters</span>
        </div>

        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12, marginBottom: 12 }}>
            {/* Phone */}
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Phone Number</div>
              <div style={{ position: 'relative' }}>
                <Search size={12} color={T.muted} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="sol-input" type="text" placeholder="Enter phone number…" value={searchPhone} onChange={e => setSearchPhone(e.target.value)} />
              </div>
            </div>

            {/* Start Date */}
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Start Date</div>
              <div style={{ position: 'relative' }}>
                <Calendar size={12} color={T.muted} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }} />
                <input className="sol-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
            </div>

            {/* End Date */}
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>End Date</div>
              <div style={{ position: 'relative' }}>
                <Calendar size={12} color={T.muted} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }} />
                <input className="sol-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Bottom row: checkbox + buttons */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <label className="sol-checkbox-wrap">
              <input
                type="checkbox"
                className="sol-checkbox"
                checked={noJobOrders}
                onChange={e => setNoJobOrders(e.target.checked)}
              />
              <span style={{ fontSize: 12, color: T.text, fontWeight: 500 }}>Show only orders without job orders</span>
            </label>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="sol-btn sol-btn-apply"
                onClick={() => { setCurrentPage(1); fetchSalesOrders(1, debouncedPhone, noJobOrders, startDate, endDate); }}
              >
                <Filter size={12} /> Apply Filter
              </button>
              <button className="sol-btn sol-btn-clear" onClick={handleClearFilters}>
                <X size={12} /> Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── ORDERS TABLE ────────────────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
        {/* head */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <List size={13} color={T.teal} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Sales Orders List</span>
          </div>
          <span style={{ fontSize: 10, color: T.teal, fontFamily: T.mono, fontWeight: 700, background: 'rgba(34,211,165,0.10)', border: '1px solid rgba(34,211,165,0.22)', padding: '3px 10px', borderRadius: 5 }}>
            {orders.length} orders
          </span>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '52px 20px', gap: 12 }}>
            <Loader size={20} color={T.blue} style={{ animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 13, color: T.muted }}>Loading orders…</span>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th className="sol-th">Cust ID</th>
                  <th className="sol-th">Customer</th>
                  <th className="sol-th">Contact</th>
                  <th className="sol-th sol-th-sort" onClick={() => handleSort('cleaning_date')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      Cleaning Date
                      {sortConfig.key === 'cleaning_date'
                        ? sortConfig.direction === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />
                        : <span style={{ opacity: 0.3 }}><ChevronDown size={10} /></span>
                      }
                    </div>
                  </th>
                  <th className="sol-th">System</th>
                  <th className="sol-th">Frequency</th>
                  <th className="sol-th">Amount</th>
                  <th className="sol-th" style={{ textAlign: 'center' }}>Status</th>
                  <th className="sol-th" style={{ textAlign: 'center' }}>Jobs</th>
                  <th className="sol-th" style={{ textAlign: 'center' }}>Payment</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '52px 20px', textAlign: 'center', color: T.muted, fontSize: 13 }}>
                      <div style={{ fontSize: 42, marginBottom: 12 }}>📭</div>
                      No sales orders found.
                    </td>
                  </tr>
                ) : (
                  orders.map((order, index) => {
                    const key = order.job_order_id ? `${order.id}-${order.job_order_id}` : `${order.id}-${index}`;
                    return (
                      <tr key={key} className="sol-tr">

                        {/* Cust ID */}
                        <td className="sol-td">
                          <span style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 700, color: T.blue }}>#{order.cid}</span>
                        </td>

                        {/* Customer */}
                        <td className="sol-td">
                          <div style={{ fontWeight: 600, color: T.text, fontSize: 12 }}>{order.client_name}</div>
                          {order.client_city && <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{order.client_city}</div>}
                        </td>

                        {/* Contact */}
                        <td className="sol-td">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.muted }}>
                            <Phone size={10} /> {order.phone_number}
                          </div>
                        </td>

                        {/* Cleaning Date */}
                        <td className="sol-td">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.text }}>
                            <Calendar size={10} color={T.muted} />
                            <span style={{ fontFamily: T.mono }}>{new Date(order.cleaning_date).toLocaleDateString()}</span>
                          </div>
                        </td>

                        {/* System size */}
                        <td className="sol-td">
                          <span style={{
                            background: 'rgba(59,130,246,0.10)', color: '#60A5FA',
                            border: '1px solid rgba(59,130,246,0.25)',
                            padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, fontFamily: T.mono,
                          }}>
                            {order.system_size} KW
                          </span>
                        </td>

                        {/* Frequency */}
                        <td className="sol-td">
                          <span style={{ fontSize: 11, color: T.muted }}>{order.frequency}</span>
                        </td>

                        {/* Amount */}
                        <td className="sol-td">
                          <span style={{ fontSize: 13, fontWeight: 800, color: T.teal, fontFamily: T.mono }}>
                            Rs {parseFloat(order.final_amount).toLocaleString()}
                          </span>
                        </td>

                        {/* Status + overdue indicator */}
                        <td className="sol-td" style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <StatusBadge status={order.status} />
                            {order.joborderpastdue ? (
                              <span title="Job order is overdue">
                                <AlertTriangle size={13} color={T.red} />
                              </span>
                            ) : (
                              <span title="Upcoming job">
                                <Clock size={13} color={T.blue} />
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Jobs action */}
                        <td className="sol-td" style={{ textAlign: 'center' }}>
                          {noJobOrders ? (
                            !order.hasjoborders && (
                              <button className="sol-btn sol-btn-gen" onClick={() => handleGenerateJobOrderButtonClick(order.id, order.cleaning_date, order.frequency)}>
                                <Zap size={10} /> Generate
                              </button>
                            )
                          ) : order.hasjoborders ? (
                            <button className="sol-btn sol-btn-view" onClick={() => handleViewJobOrderButtonClick(order.id)}>
                              <Eye size={10} /> View Jobs
                            </button>
                          ) : (
                            <button className="sol-btn sol-btn-gen" onClick={() => handleGenerateJobOrderButtonClick(order.id, order.cleaning_date, order.frequency)}>
                              <Zap size={10} /> Generate
                            </button>
                          )}
                        </td>

                        {/* Payment */}
                        <td className="sol-td" style={{ textAlign: 'center' }}>
                          {order.is_paid ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: T.green, background: 'rgba(52,211,153,0.10)', border: '1px solid rgba(52,211,153,0.25)', padding: '3px 9px', borderRadius: 5, fontFamily: T.mono }}>
                              <CheckCircle size={10} /> Paid
                            </span>
                          ) : (
                            <button className="sol-btn sol-btn-pay" onClick={() => handleShowMarkPaidModal(order.id)}>
                              <CreditCard size={10} /> Mark Paid
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── PAGINATION ──────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, marginBottom: 20 }}>
          <button className="sol-page-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <ChevronsLeft size={12} />
          </button>
          <button className="sol-page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <ChevronLeft size={12} />
          </button>

          {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(p => (
            <button key={p} className={`sol-page-btn${currentPage === p ? ' active' : ''}`} onClick={() => handlePageChange(p)}>
              {p}
            </button>
          ))}

          <button className="sol-page-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            <ChevronRight size={12} />
          </button>
          <button className="sol-page-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
            <ChevronsRight size={12} />
          </button>
        </div>
      )}

      {/* ── JOB ORDERS MODAL ────────────────────────────────────── */}
      {showJobOrdersModal && (
        <div className="sol-overlay" onClick={() => setShowJobOrdersModal(false)}>
          <div className="sol-modal" onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Briefcase size={14} color={T.blue} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Job Orders</div>
                  <div style={{ fontSize: 10, color: T.muted, fontFamily: T.mono }}>Sales Order #{currentSalesOrderId}</div>
                </div>
              </div>
              <button onClick={() => setShowJobOrdersModal(false)} style={{ background: 'var(--nd-w06)', border: '1px solid var(--nd-w10)', borderRadius: 7, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.muted }}>
                <X size={14} />
              </button>
            </div>

            {/* Modal body */}
            <div className="sol-modal-body">
              {loadingJobOrders ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: 12 }}>
                  <Loader size={18} color={T.blue} style={{ animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ fontSize: 12, color: T.muted }}>Loading job orders…</span>
                </div>
              ) : jobOrders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: 42, marginBottom: 12 }}>📭</div>
                  <div style={{ fontSize: 13, color: T.muted }}>No job orders found for this sales order.</div>
                </div>
              ) : (
                <>
                  {/* Assigned Teams */}
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 11, padding: '14px 16px', marginBottom: 16 }}>
                    <ModalSecHead Icon={Users} color={T.purple} title="Assigned Teams" />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {jobOrders.map(job => (
                        <div key={job.id} style={{ padding: '8px 12px', background: T.surf3, border: `1px solid ${T.border}`, borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: T.muted, fontFamily: T.mono }}>Job #{job.id}</span>
                          {job.team_assigned ? (
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#60A5FA', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', padding: '2px 9px', borderRadius: 5, fontFamily: T.mono }}>
                              {job.assigned_team}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, color: T.muted, fontStyle: 'italic' }}>No team assigned</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Job Orders Table */}
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 11, overflow: 'hidden' }}>
                    <ModalSecHead Icon={Briefcase} color={T.blue} title="Job Order Details" />
                    <div style={{ padding: '0 0 4px', overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            {['Job ID','Status','Scheduled Date','Details'].map(h => (
                              <th key={h} style={{ padding: '8px 12px', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--nd-tx30)', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap', background: T.surf3 }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {jobOrders.map(job => (
                            <tr key={job.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                              <td style={{ padding: '10px 12px', fontFamily: T.mono, fontSize: 12, fontWeight: 700, color: T.blue }}>#{job.id}</td>
                              <td style={{ padding: '10px 12px' }}><StatusBadge status={job.status} /></td>
                              <td style={{ padding: '10px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.muted }}>
                                  <Calendar size={10} /> <span style={{ fontFamily: T.mono }}>{new Date(job.scheduled_date).toLocaleDateString()}</span>
                                </div>
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                {job.assigned_team ? (
                                  <span style={{ fontSize: 11, color: T.text }}>Team: <strong style={{ color: T.blue }}>{job.assigned_team}</strong></span>
                                ) : (
                                  <span style={{ fontSize: 11, color: T.muted, fontStyle: 'italic' }}>No team assigned</span>
                                )}
                                {job.status === 'Cancelled' && (
                                  <div style={{ marginTop: 6, padding: '6px 10px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.22)', borderRadius: 6, borderLeft: '3px solid #F87171' }}>
                                    <div style={{ fontSize: 11, color: T.red, fontWeight: 700 }}>Cancelled by {job.cancelled_by}</div>
                                    <div style={{ fontSize: 10, color: 'rgba(248,113,113,0.65)', marginTop: 3, fontFamily: T.mono }}>{new Date(job.cancelled_at).toLocaleString()}</div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal footer */}
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button className="sol-btn sol-btn-clear" onClick={() => setShowJobOrdersModal(false)} style={{ padding: '7px 18px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MARK PAID MODAL (external — unchanged) ──────────────── */}
      <MarkPaidModal
        show={showMarkPaidModal}
        onHide={() => setShowMarkPaidModal(false)}
        salesOrderId={currentSalesOrderId}
        onSuccess={() => fetchSalesOrders(currentPage, debouncedPhone, noJobOrders, startDate, endDate)}
      />
    </div>
  );
};

export default SalesOrdersList;